(ns manage.content-configuration-page.content-configuration.db
  (:require [common.content-function.core :as cfn]
            [common.db.interceptors :refer [forwarded-event]]
            [common.ui.validation.db :refer [transform-entity-validate]]
            [manage.content-configuration-page.content-configuration.export
             :as export]
            [manage.content-configuration-page.content-configuration.import
             :as import]
            [manage.content-configuration-page.context-source.shared
             :refer [pull-pattern-context-source]]
            [manage.content-configuration-page.placeholder.db
             :refer [pull-pattern-placeholder]]
            [manage.content-configuration-page.rule.db :refer [pull-pattern-rule]]
            [re-frame.core :as rf]))

(def pull-pattern-content-configuration
  ['*
   {:content-configuration/context-source pull-pattern-context-source}
   {:content-configuration/rule pull-pattern-rule}
   {:content-configuration/placeholder pull-pattern-placeholder}])

(rf/reg-event-fx ::handle-import-failure
  [(rf/inject-cofx :sub :resource.content-function/value)]
  (fn [{payload :resource.content-function/value} [_ error]]
    {:fx [[:dispatch [:page.content-configuration/import-status :failure error]]
          [:sentry/capture [error {:contexts {"Content configuration payload" payload}}]]]}))

(rf/reg-event-fx ::handle-import-success
  [(rf/inject-cofx :sub [:resource.content-function-version/active-version? "published"])]
  (fn [{published? :resource.content-function-version/active-version?} _]
    {:fx [(when-not published? [:page.content-configuration/register-mutation-listener nil])
          [:dispatch [:page.content-configuration/import-status :success]]
          [:dispatch [:manage.content-configuration-page.context-source.db/load-schema-output-structures]]
          [:dispatch [:manage.content-configuration-page.context-source.db/load-schema-output-data]]
          [:dispatch [:manage.content-configuration-page.context-source.db/load-param-schemas]]
          ;; temporary fix; after import the current entity view cfg is outdated
          [:dispatch [:side-menu/default :placeholders]]]}))

(rf/reg-event-fx ::import
  [forwarded-event
   (rf/inject-cofx :sub :resource.content-function/value)]
  (fn [{ext-content-function :resource.content-function/value} _]
    (let [datoms (conj (import/content-configuration "default" ext-content-function)
                       ;; completely remove existing content configuration before
                       ;; importing new data to avoid upsert behaviour
                       [:db/retractEntity [:content-configuration/id "default"]])]
      {:fx [[:dispatch [:manage.content-configuration-page.view.save/save-success]]
            [:page.content-configuration/unregister-mutation-listener nil]
            [:posh/transact! [datoms {:on-error [::handle-import-failure]
                                      :on-success [::handle-import-success]}]]]})))

(rf/reg-event-fx ::handle-export-failure
  [(rf/inject-cofx :sub :manage.content-configuration-page.view.save/changelog)
   (rf/inject-cofx :sub :manage.content-configuration-page.view.save/unsaved-transactions)]
  (fn [{:manage.content-configuration-page.view.save/keys [changelog unsaved-transactions]} [_ result]]
    {:fx [[:dispatch [:manage.content-configuration-page.view.save/save-failure]]
          [:sentry/capture ["Could not save content configuration"
                            {:contexts
                             {"Content configuration export"
                              {:changelog (map str changelog)
                               :unsaved-transactions (->> unsaved-transactions
                                                          vals
                                                          (apply concat)
                                                          (map (comp str (juxt :tx :added :e :a :v))))
                               :server-response (get-in result [:value :error :spec :error :message])}}}]]
          [:dispatch [:modal/open {:id :manage.content-configuration-page.view.save/cfn-save-error-results
                                   :props result}]]]}))

(rf/reg-event-fx ::handle-export-success
  (fn [_ _]
    {:fx [[:dispatch [:manage.content-configuration-page.view.save/save-success]]
          [:dispatch [:toaster-bus/add {:id :save-content-configuration
                                        :type :success
                                        :message "Content configuration has been saved"}]]]}))

(defn persist?
  "determine if change to internal db, exported as `new-cfn` should be sent to CFN
  service for storage"
  [last-received-cfn-adset-id current-adset-id last-received-cfn last-exported-cfn new-cfn]
  (let [last-received-cfn (some-> last-received-cfn cfn/keywordify)
        adset-id-match? (or (nil? last-received-cfn-adset-id)
                            (= current-adset-id last-received-cfn-adset-id))]
    (and adset-id-match?
         (some? last-received-cfn)
         (not= new-cfn (or last-exported-cfn last-received-cfn)))))

(rf/reg-event-db :page.content-configuration/set-last-exported-cfn
  [(rf/path :page.content-configuration :last-exported-cfn)]
  (fn [_ [_ cfn]]
    cfn))

(rf/reg-sub :page.content-configuration/last-exported-cfn
  :<- [:page.content-configuration]
  (fn [db _]
    (:last-exported-cfn db)))

(rf/reg-event-fx
  ;; create cofx to run this in the background?
  ::export
  [(rf/inject-cofx :ds)
   (rf/inject-cofx :sub :context)
   (rf/inject-cofx :sub [:resource.content-function/one])
   (rf/inject-cofx :sub [:page.content-configuration/last-exported-cfn])
   (rf/inject-cofx :sub [:resource.content-function-version/active-version
                         {:only-staged true}])]
  (fn [{:keys [ds db]
        {:keys [adset-id]} :context
        {last-received-cfn :value stored-adset-id :adset-id} :resource.content-function/one
        last-exported-cfn :page.content-configuration/last-exported-cfn
        :resource.content-function-version/keys [active-version]} _]
    (let [cfn (export/ext-content-function ds [:content-configuration/id "default"] (:meta last-received-cfn))]
      (if (persist? stored-adset-id adset-id last-received-cfn last-exported-cfn cfn)
        {:fx [[:dispatch [:page.content-configuration/set-last-exported-cfn cfn]]
              [:dispatch [:manage.content-configuration-page.view.save/start-save]]
              [:dispatch [:resource.content-function/put-one
                          (cfn/->payload cfn)
                          {:stage (:name active-version)
                           :on-success [::handle-export-success]
                           :on-failure [::handle-export-failure]}]]]}
        {:db (dissoc db :page.content-configuration/log-unsaved-transactions :page.content-configuration/save-in-progress?)}))))

(rf/reg-event-fx ::create-draft
  [(rf/inject-cofx :ds)
   (rf/inject-cofx :sub [:resource.content-function/meta])]
  (fn [{:keys [ds] :resource.content-function/keys [meta]} _]
    {:fx [[:dispatch [:resource.content-function/create-stage
                      "draft" (cfn/->payload (export/ext-content-function ds [:content-configuration/id "default"] meta))]]]}))

(rf/reg-sub :content-configuration/can-publish?
  (fn [[_ cc]]
    (rf/subscribe [:validation/results-from-db cc :content-configuration pull-pattern-content-configuration]))
  (fn [validation-results _]
    (not-any? #(= (:stage %) :publish-blocking) validation-results)))

(rf/reg-event-fx ::publish-if-valid
  [(rf/inject-cofx :sub [:content-configuration/can-publish? [:content-configuration/id "default"]])]
  (fn [{:content-configuration/keys [can-publish?]} _]
    {:fx [[:dispatch (if can-publish?
                       [:resource.content-function/publish-stage "draft"]
                       [:modal/open {:id :manage.content-configuration-page.view.content/block-publishing}])]]}))

(defmethod transform-entity-validate :content-configuration
  [_ cc]
  (-> cc
      (assoc-in [:adset/_content-configuration :adset/assigned-template :template-revision/template-placeholder]
                @(rf/subscribe [:manage.content-configuration-page.placeholder.db/template-placeholders]))
      (assoc :adset/workflow
             @(rf/subscribe [:resource.adset/current-workflow]))))
